import { getErrorMessage } from '../utils/errorMessage';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, BookOpen, Download, RefreshCw, Trash2, Pencil, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../utils/api';

/* ── Danmaku ── */
const DANMAKU_TEXTS = [
  '主角即将觉醒！', '反派太嚣张了', '这个钩子绝了', '第一集必须追',
  'AI在疯狂创作中', '这个冲突设计绝', '投流必爆款', '剧情反转ing',
  '台词太带劲了', '镜头感满分', '观众要付费了', '已经在想第二季',
  '前三集钩子锁死', '主角光环全开', '反转！反转！', '下一集呢！！',
];

function Danmaku() {
  const [items, setItems] = useState([]);
  const counter = useRef(0);
  useEffect(() => {
    const colors = ['#a78bfa', '#34d399', '#fb923c', '#f472b6', '#60a5fa', '#fbbf24'];
    const add = () => {
      const id = counter.current++;
      setItems(p => [...p.slice(-25), {
        id,
        text: DANMAKU_TEXTS[Math.floor(Math.random() * DANMAKU_TEXTS.length)],
        top: 8 + Math.random() * 76,
        duration: 7 + Math.random() * 5,
        color: colors[Math.floor(Math.random() * colors.length)],
      }]);
    };
    add();
    const t = setInterval(add, 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 1 }}>
      {items.map(item => (
        <div key={item.id} style={{
          position: 'absolute', top: `${item.top}%`, right: '-220px',
          color: item.color, fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap',
          animation: `danmaku ${item.duration}s linear forwards`, opacity: 0.75,
        }}>{item.text}</div>
      ))}
      <style>{`@keyframes danmaku{from{transform:translateX(0)}to{transform:translateX(calc(-100vw - 300px))}}`}</style>
    </div>
  );
}

/* ── Generating stages ── */
const STAGES = [
  { key: 'idea',      label: '创意生成', desc: '分析输入，生成爆款方向',      duration: 30  },
  { key: 'character', label: '人物构建', desc: '塑造主角与反派',              duration: 25  },
  { key: 'arc',       label: '剧情规划', desc: '规划多幕结构',                duration: 30  },
  { key: 'outline',   label: '分集大纲', desc: '生成每集剧情与钩子',          duration: 60  },
  { key: 'rhythm',    label: '节奏优化', desc: '强化爽点密度',                duration: 40  },
  { key: 'script',    label: '剧本生成', desc: '生成分镜台词',                duration: 180 },
];

function GeneratingView({ idea, createdAt, episodeCount }) {
  const scriptId = window.location.pathname.split('/').pop();
  const getElapsed = () => createdAt ? Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000) : 0;
  const [elapsed, setElapsed] = useState(getElapsed);
  const [stageIdx, setStageIdx] = useState(0);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => { const t = setInterval(() => setElapsed(getElapsed), 1000); return () => clearInterval(t); });
  useEffect(() => {
    let acc = 0;
    for (let i = 0; i < STAGES.length; i++) {
      acc += STAGES[i].duration;
      if (elapsed < acc) { setStageIdx(i); return; }
    }
    setStageIdx(STAGES.length - 1);
  }, [elapsed]);

  const total = STAGES.reduce((s, st) => s + st.duration, 0);
  const progress = Math.min((elapsed / total) * 100, 95);
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;

  const cancel = async () => {
    if (!window.confirm('确定要停止生成吗？')) return;
    setCancelling(true);
    try { await api.post(`/scripts/${scriptId}/cancel`); window.location.reload(); }
    catch { setCancelling(false); }
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#0e0e10' }}>
      <Danmaku />
      <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 560, padding: '0 24px' }}>
        {/* Spinner */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 72, height: 72, borderRadius: '50%', background: 'rgba(139,92,246,0.12)', border: '2px solid rgba(139,92,246,0.3)', marginBottom: 20, animation: 'pulsering 2s ease-in-out infinite' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid transparent', borderTopColor: '#8b5cf6', animation: 'spin 0.8s linear infinite' }} />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#f1f0ff', marginBottom: 8 }}>AI 正在疯狂创作中</h2>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 8, maxWidth: 380, margin: '0 auto 8px' }}>{idea}</p>
          <p style={{ fontSize: 13, color: '#8b5cf6', fontWeight: 600 }}>
            {mins}:{secs.toString().padStart(2, '0')} 已用时
          </p>
          {episodeCount > 0 && (
            <p style={{ fontSize: 14, color: '#34d399', fontWeight: 600, marginTop: 8 }}>已完成 {episodeCount} 集</p>
          )}
        </div>

        {/* Progress */}
        <div style={{ marginBottom: 20, background: 'rgba(139,92,246,0.06)', borderRadius: 16, padding: '16px 20px', border: '1px solid rgba(139,92,246,0.15)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 13 }}>
            <span style={{ color: '#a78bfa', fontWeight: 600 }}>◉ {STAGES[stageIdx].label}</span>
            <span style={{ color: '#6b7280' }}>{Math.round(progress)}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 9999, background: 'rgba(139,92,246,0.12)', overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ height: '100%', borderRadius: 9999, background: 'linear-gradient(90deg,#6d28d9,#a78bfa)', width: `${progress}%`, transition: 'width 1s ease' }} />
          </div>
          <p style={{ fontSize: 11, color: '#6b7280' }}>{STAGES[stageIdx].desc}</p>
        </div>

        {/* Stage grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 24 }}>
          {STAGES.map((st, i) => (
            <div key={st.key} style={{
              padding: '10px 12px', borderRadius: 12,
              background: i < stageIdx ? 'rgba(52,211,153,0.07)' : i === stageIdx ? 'rgba(139,92,246,0.1)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${i < stageIdx ? 'rgba(52,211,153,0.25)' : i === stageIdx ? 'rgba(139,92,246,0.35)' : 'rgba(255,255,255,0.06)'}`,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 3, color: i < stageIdx ? '#34d399' : i === stageIdx ? '#a78bfa' : '#4b5563' }}>
                {i < stageIdx ? '✓ ' : i === stageIdx ? '● ' : ''}{st.label}
              </div>
              <div style={{ fontSize: 10, color: '#4b5563', lineHeight: 1.5 }}>{st.desc}</div>
            </div>
          ))}
        </div>

        <p style={{ textAlign: 'center', fontSize: 12, color: '#4b5563' }}>可以关闭页面，完成后在剧本管理查看结果</p>
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button onClick={cancel} disabled={cancelling} style={{ fontSize: 12, color: '#ef4444', background: 'none', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '6px 16px', cursor: 'pointer', opacity: cancelling ? 0.5 : 1 }}>
            {cancelling ? '停止中...' : '停止生成'}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes pulsering { 0%,100%{box-shadow:0 0 0 0 rgba(139,92,246,0.3)} 50%{box-shadow:0 0 0 14px rgba(139,92,246,0)} }
      `}</style>
    </div>
  );
}

/* ── Rhythm tag ── */
const RHYTHM = {
  slow:   { label: '铺垫',  cls: 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20' },
  normal: { label: '推进',  cls: 'bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20' },
  strong: { label: '强冲突',cls: 'bg-red-50 text-red-600 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20' },
};

/* ── Main ── */
export default function ScriptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState(0);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue]   = useState('');
  const [regenerating, setRegenerating] = useState(null);

  useEffect(() => {
    let timer;
    const load = () => {
      api.get(`/scripts/${id}`).then(r => {
        setData(r.data);
        if (r.data.status === 'generating') timer = setTimeout(load, 5000);
      }).finally(() => setLoading(false));
    };
    load();
    return () => clearTimeout(timer);
  }, [id]);

  const handleDeleteEp = async epNum => {
    if (!window.confirm(`确定要删除第 ${epNum} 集吗？`)) return;
    try {
      await api.delete(`/scripts/${id}/episodes/${epNum}`);
      setData(p => ({ ...p, episodes: p.episodes.filter(e => e.episode_number !== epNum), episode_count: p.episode_count - 1 }));
      setActiveTab(0);
    } catch { alert('删除失败'); }
  };

  const handleRegenerateEp = async epNum => {
    if (!window.confirm(`重新生成第 ${epNum} 集将消耗 1 点额度，确定吗？`)) return;
    setRegenerating(epNum);
    try { await api.post(`/scripts/${id}/episodes/${epNum}/regenerate`); alert('已提交，约 30 秒后刷新查看。'); }
    catch (e) { alert(getErrorMessage(e, '重新生成失败')); }
    finally { setRegenerating(null); }
  };

  const handleExport = () => {
    const token = localStorage.getItem('token');
    fetch(`/api/export/${id}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url;
        a.download = (data.title || '剧本').slice(0, 20) + '.docx';
        a.click();
      });
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 size={24} className="animate-spin text-gray-300 dark:text-gray-600" />
    </div>
  );
  if (!data) return <div className="p-8 text-gray-400">剧本不存在</div>;
  if (data.status === 'generating') return (
    <GeneratingView idea={data.idea} createdAt={data.created_at} episodeCount={data.episodes?.length || data.episode_count || 0} />
  );

  const ep = data.episodes?.[activeTab];

  return (
    <div className="max-w-4xl mx-auto px-8 py-8">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors group"
        >
          <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
          返回剧本管理
        </button>
        {data.episodes?.length > 0 && (
          <button onClick={handleExport}
            className="flex items-center gap-2 text-sm btn-secondary px-3 py-1.5">
            <Download size={14} />下载剧本
          </button>
        )}
      </div>

      {/* Title */}
      <div className="mb-8">
        <div className="flex items-start gap-2 mb-2">
          {editingTitle ? (
            <input
              autoFocus
              value={titleValue}
              onChange={e => setTitleValue(e.target.value)}
              onBlur={async () => {
                if (titleValue.trim() && titleValue !== data.title) {
                  await api.patch(`/scripts/${id}/title`, { title: titleValue });
                  setData(p => ({ ...p, title: titleValue }));
                }
                setEditingTitle(false);
              }}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingTitle(false); }}
              className="text-2xl font-bold bg-[#f7f3eb] dark:bg-white/5 border border-[#8c7db0]/50
                         rounded-xl px-3 py-1 outline-none focus:ring-2 focus:ring-[#8c7db0]/30 flex-1"
            />
          ) : (
            <>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{data.title}</h1>
              <button
                onClick={() => { setTitleValue(data.title); setEditingTitle(true); }}
                className="p-1 rounded-lg text-gray-300 dark:text-gray-600 hover:text-[#6f6196] hover:bg-[#eee9de] dark:hover:bg-white/[0.08] transition-all mt-1">
                <Pencil size={14} />
              </button>
            </>
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{data.idea}</p>
        {data.episode_count > 0 && (
          <span className="inline-block mt-2 text-xs text-gray-400 bg-[#eee9de] dark:bg-white/[0.05] px-2.5 py-1 rounded-full">
            共 {data.episode_count} 集
          </span>
        )}
      </div>

      {data.episodes?.length > 0 ? (
        <div className="space-y-6">
          {/* Episode tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-hide">
            {data.episodes.map((ep, i) => (
              <button
                key={ep.id}
                onClick={() => setActiveTab(i)}
                className={`shrink-0 px-3.5 py-1.5 rounded-xl text-sm font-medium transition-all
                  ${activeTab === i
                    ? 'bg-[#8c7db0] text-white shadow-sm shadow-[#8c7db0]/20'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-[#eee9de] dark:hover:bg-white/[0.05]'
                  }`}
              >
                第 {ep.episode_number} 集
              </button>
            ))}
          </div>

          {/* Episode content */}
          {ep && (
            <div className="card p-6 space-y-6">
              {/* Episode header */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#eee9de] dark:bg-white/[0.08] border border-[#ded6c8] dark:border-white/[0.12]
                                  flex items-center justify-center text-sm font-bold text-[#6f6196] dark:text-[#b9afe7] shrink-0">
                    {ep.episode_number}
                  </div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{ep.title}</h2>
                  {ep.rhythm_tag && (
                    <span className={`text-xs px-2.5 py-0.5 rounded-full border font-medium ${RHYTHM[ep.rhythm_tag]?.cls || RHYTHM.normal.cls}`}>
                      {RHYTHM[ep.rhythm_tag]?.label || ep.rhythm_tag}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleRegenerateEp(ep.episode_number)}
                    disabled={regenerating === ep.episode_number}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl
                               bg-[#eee9de] dark:bg-white/[0.05] border border-[#ded6c8] dark:border-white/[0.08]
                               text-gray-500 dark:text-gray-400 hover:text-[#6f6196] dark:hover:text-[#b9afe7]
                               hover:border-[#c9bedc] dark:hover:border-white/[0.16]
                               disabled:opacity-40 transition-all">
                    <RefreshCw size={12} className={regenerating === ep.episode_number ? 'animate-spin' : ''} />
                    {regenerating === ep.episode_number ? '生成中...' : '重新生成'}
                  </button>
                  <button
                    onClick={() => handleDeleteEp(ep.episode_number)}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl
                               bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20
                               text-red-400 hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20 transition-all">
                    <Trash2 size={12} />删除本集
                  </button>
                </div>
              </div>

              {/* Opening hook */}
              {ep.opening_hook && (
                <div className="p-4 bg-[#eee9de] dark:bg-white/[0.08] rounded-xl border-l-4 border-[#8c7db0]">
                  <p className="text-xs font-semibold text-[#6f6196] dark:text-[#b9afe7] mb-1.5 uppercase tracking-wider">开场钩子</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{ep.opening_hook}</p>
                </div>
              )}

              {/* Scenes */}
              {ep.scenes?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">分镜场景</p>
                  <div className="space-y-4">
                    {ep.scenes.map((scene, si) => (
                      <div key={si} className="bg-[#fbfaf6] dark:bg-white/[0.03] border border-[#ded6c8] dark:border-white/[0.06] rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xs font-bold text-[#6f6196] dark:text-[#b9afe7] bg-[#eee9de] dark:bg-white/[0.08] px-2 py-0.5 rounded-lg">
                            场景 {scene.scene_number || si + 1}
                          </span>
                          {scene.location && (
                            <span className="text-xs text-gray-400">{scene.location}</span>
                          )}
                        </div>
                        {scene.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">{scene.description}</p>
                        )}
                        {scene.dialogues?.length > 0 && (
                          <div className="space-y-3 border-l-2 border-[#c9bedc] dark:border-white/[0.12] pl-4">
                            {scene.dialogues.map((d, di) => (
                              <div key={di} className="space-y-1">
                                <p className="text-sm">
                                  <span className="font-semibold text-[#6f6196] dark:text-[#b9afe7]">{d.character}：</span>
                                  <span className="text-gray-700 dark:text-gray-300">{d.line}</span>
                                </p>
                                {d.action && (
                                  <p className="text-xs text-gray-400 italic pl-2">[{d.action}]</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ending hook */}
              {ep.ending_hook && (
                <div className="p-4 bg-[#f2eadb] dark:bg-white/[0.08] rounded-xl border-l-4 border-[#c2a079]">
                  <p className="text-xs font-semibold text-[#7f6b45] dark:text-[#d4c7a8] mb-1.5 uppercase tracking-wider">结尾钩子</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{ep.ending_hook}</p>
                </div>
              )}

              {/* Prev / Next navigation */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-white/[0.06]">
                <button
                  onClick={() => setActiveTab(t => Math.max(0, t - 1))}
                  disabled={activeTab === 0}
                  className="flex items-center gap-1.5 text-sm btn-ghost disabled:opacity-30 disabled:cursor-not-allowed">
                  <ChevronLeft size={15} />上一集
                </button>
                <span className="text-xs text-gray-400">{activeTab + 1} / {data.episodes.length}</span>
                <button
                  onClick={() => setActiveTab(t => Math.min(data.episodes.length - 1, t + 1))}
                  disabled={activeTab === data.episodes.length - 1}
                  className="flex items-center gap-1.5 text-sm btn-ghost disabled:opacity-30 disabled:cursor-not-allowed">
                  下一集<ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
              <div className="w-16 h-16 rounded-2xl bg-[#eee9de] dark:bg-white/[0.04] border border-[#ded6c8] dark:border-white/[0.07] flex items-center justify-center">
            <BookOpen size={24} className="text-gray-300 dark:text-gray-600" />
          </div>
          <p className="text-gray-400 text-sm">暂无内容</p>
        </div>
      )}
    </div>
  );
}
